import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import AuthService from "../services/auth-services";
import { checkBrowserCompatibility } from "../utils/browserUtils";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const mergeUserWithCurrency = (userData) => {
  if (!userData) return userData;
  const savedCurrency = localStorage.getItem("userCurrency");
  if (savedCurrency && !userData.currency) {
    return { ...userData, currency: savedCurrency };
  }
  if (!userData.currency) {
    return { ...userData, currency: "RON" };
  }
  return userData;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isUserInactive, setIsUserInactive] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const lastActivityRef = useRef(Date.now());
  const [browserCompatibility, setBrowserCompatibility] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);

  const checkAuthStatus = useCallback(
    async (isInitialCheck = false) => {
      try {
        if (isInitialCheck) {
          setLoading(true);
        }

        const response = await AuthService.checkAuthStatus();

        if (response.isAuthenticated && response.user) {
          if (response.user.isActive === false) {
            setUser(response.user);
            setIsAuthenticated(true);
            setIsUserInactive(true);
            window.location.href = "/access-denied?reason=account_disabled";
            return;
          } else {
            const userWithCurrency = mergeUserWithCurrency(response.user);

            setUser(userWithCurrency);
            setIsAuthenticated(true);
            setIsUserInactive(false);
            sessionStorage.removeItem("returnUrl");
            sessionStorage.removeItem("googleOAuthInitiated");
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsUserInactive(false);
        }
      } catch (error) {
        const isAuthError =
          error.message?.includes("401") || error.message?.includes("403");
        const isNetworkError =
          error.message?.includes("Network") ||
          error.message?.includes("fetch") ||
          error.message?.includes("TypeError");
        const isCorsError =
          error.message?.includes("CORS") ||
          error.message?.includes("cross-origin");

        if (isAuthError) {
          setUser(null);
          setIsAuthenticated(false);
          setIsUserInactive(false);
        } else if (isNetworkError || isCorsError) {
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsUserInactive(false);
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const compatibility = checkBrowserCompatibility();
    setBrowserCompatibility(compatibility);
  }, []);

  useEffect(() => {
    checkAuthStatus(true);
  }, [checkAuthStatus]);

  useEffect(() => {
    lastActivityRef.current = lastActivity;
  }, [lastActivity]);

  useEffect(() => {
    if (!isAuthenticated || isUserInactive) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity > 10 * 60 * 1000) {
          checkAuthStatus();
        }
        const now = Date.now();
        lastActivityRef.current = now;
        setLastActivity(now);
      }
    };

    const handleFocus = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      setLastActivity(now);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isAuthenticated, isUserInactive, checkAuthStatus]);

  const login = useCallback(async () => {
    try {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const returnUrl = currentPath + currentSearch;

      if (returnUrl !== "/auth" && returnUrl !== "/auth/") {
        sessionStorage.setItem("returnUrl", returnUrl);
      } else {
        sessionStorage.setItem("returnUrl", "/");
      }

      await AuthService.initiateGoogleLogin();
    } catch (error) {
      throw error;
    }
  }, []);

  const loginWithEmailPassword = useCallback(async (email, password) => {
    try {
      const response = await AuthService.loginWithEmailPassword(
        email,
        password
      );

      if (response.user) {
        if (response.user.isActive === false) {
          setUser(response.user);
          setIsAuthenticated(true);
          setIsUserInactive(true);
          window.location.href = "/access-denied?reason=account_disabled";
          return;
        } else {
          const userWithCurrency = mergeUserWithCurrency(response.user);
          setUser(userWithCurrency);
          setIsAuthenticated(true);
          setIsUserInactive(false);
          setLastActivity(Date.now());
          return response;
        }
      } else {
        throw new Error(response.message || "Login failed");
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await AuthService.logout();
    } catch (error) {
      /* still clear local state */
    } finally {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      sessionStorage.removeItem("returnUrl");
      sessionStorage.removeItem("googleOAuthInitiated");
      setUser(null);
      setIsAuthenticated(false);
      setIsUserInactive(false);
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      const response = await AuthService.forgotPassword(email);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    try {
      const response = await AuthService.resetPassword(token, password);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  const validateResetToken = useCallback(async (token) => {
    try {
      const response = await AuthService.validateResetToken(token);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  const refreshAuthStatus = useCallback(() => {
    return checkAuthStatus();
  }, [checkAuthStatus]);

  const startNavigation = useCallback(() => {
    setIsNavigating(true);
  }, []);

  const endNavigation = useCallback(() => {
    setIsNavigating(false);
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    isUserInactive,
    browserCompatibility,
    isNavigating,
    login,
    loginWithEmailPassword,
    logout,
    updateUser,
    checkAuthStatus,
    refreshAuthStatus,
    forgotPassword,
    resetPassword,
    validateResetToken,
    startNavigation,
    endNavigation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
